import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useSelector } from 'hooks/redux'
import { DESIGN, ACTIVE, OBSOLETE } from 'utils/constants'
import strings from 'utils/strings'
import { themeColor } from 'utils/styles'
import {
  DesignButton,
  ActiveButton,
  ObsoleteButton,
} from 'components/feature/LifeCycleButtons'
import { PhaseConnector } from 'components/feature/LifeCycleEditor/LifeCycleEditor.style'
import {
  getCanActivateCollection,
  getCanObsoleteCollection,
} from 'selectors/authorization/checPlus'
import { getIsStatusUpdating } from 'selectors/collection'

/**
 * Interactive Lifecycle Status Visual for Collections (PBI 150666)
 * Shows the current status with clickable icons in a horizontal flow:
 * Design -> Active -> Obsolete
 *
 * Clicking on a circle triggers the onActivate/onObsolete callbacks
 * Based on LifeCycleEditor pattern from Features page
 */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const StatusTitle = styled.span`
  color: ${themeColor('blackSecondary')};
  font-size: 12px;
  font-family: Arial;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`

const StatusText = styled.span`
  color: ${themeColor('blackSecondary')};
  font-family: Arial;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px;
`

// Custom styled label for consistent positioning
const StatusLabel = styled.text`
  font-family: Arial, sans-serif;
  font-size: 12px;
  text-anchor: middle;
  fill: ${({ isActive, theme }) =>
    isActive ? theme.btnPrimaryFocusColor : theme.btnDisabledColor};
`

const getStatusLabel = (status) => {
  switch (status) {
    case DESIGN:
      return strings.design || 'Design'
    case ACTIVE:
      return strings.active || 'Active'
    case OBSOLETE:
      return strings.obsolete || 'Obsolete'
    default:
      // Format unknown status to Title Case (e.g., "DESIGN" -> "Design")
      if (!status) return strings.design || 'Design'
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  }
}

// Circle positions (center X coordinates)
const DESIGN_X = 25
const ACTIVE_X = 100
const OBSOLETE_X = 175
const CIRCLE_RADIUS = 13

const LifecycleStatusVisual = ({
  status,
  onActivateClick,
  onObsoleteClick,
}) => {
  const canActivate = useSelector(getCanActivateCollection)
  const canObsolete = useSelector(getCanObsoleteCollection)
  const isStatusUpdating = useSelector(getIsStatusUpdating)

  const isDesign = status === DESIGN
  const isActive = status === ACTIVE
  const isObsolete = status === OBSOLETE

  // Status progression: Design (0) -> Active (1) -> Obsolete (2)
  const statusIndex = isDesign ? 0 : isActive ? 1 : 2

  // Can click Active circle when in Design status and has permission
  // Note: collectionName validation happens in the click handler (PBI 150666)
  const canClickActive = Boolean(isDesign && canActivate && !isStatusUpdating)
  // Can click Obsolete circle when in Active status and has permission
  const canClickObsolete = Boolean(isActive && canObsolete && !isStatusUpdating)

  /**
   * Figma Design Rules (PBI 150666):
   *
   * Tier 0-1 (no permissions):
   * - Only show the current state icon (no line, no next state button, no labels)
   *
   * Tier 2+ (with permissions):
   * - Design mode: Show Design + Active (can activate)
   * - Active mode: Show Active + Obsolete (Tier 3 can obsolete)
   * - Obsolete mode: Show Active + Obsolete (read-only)
   */

  // Determine what to show based on permissions and current state
  // Bug 479023: Design button ONLY shown when status is Design
  // Bug 479123 Issue 18: In Obsolete mode, ONLY show Obsolete icon
  // - In Design mode: show Design + Active (if user can activate)
  // - In Active mode: show Active + Obsolete (Tier 3 can obsolete)
  // - In Obsolete mode: show ONLY Obsolete icon (no Active, no line)
  const showDesignButton = isDesign
  const showActiveButton = isDesign ? canActivate : isActive
  // Bug 479122 Issue 11: Only show Obsolete button when user has permission (Tier 3+)
  // Tier 0-2 users should not see the Obsolete button at all (not even disabled)
  const showObsoleteButton = isObsolete || (isActive && canObsolete)
  const showDesignToActiveLine = showDesignButton && showActiveButton
  const showActiveToObsoleteLine = isActive && showObsoleteButton

  // Bug 479122: Always show labels when any icon is visible (including Tier 0-1 with single icon)
  const showLabels =
    showDesignButton || showActiveButton || isActive || isObsolete

  // Calculate SVG width based on visible elements
  // Bug 479023: When in Active status, only 2 icons (Active + Obsolete) are shown
  // Bug 479123 Issue 18: When in Obsolete status, only 1 icon (Obsolete) is shown
  const getSvgWidth = () => {
    if (showDesignButton && showObsoleteButton) return 200 // 3 icons (Design + Active + Obsolete)
    if (showActiveButton && showObsoleteButton) return 125 // 2 icons (Active + Obsolete, Active status)
    if (showDesignButton && showActiveButton) return 125 // 2 icons (Design + Active)
    if (showObsoleteButton && !showActiveButton) return 50 // 1 icon only (Obsolete status)
    return 50 // 1 icon only (Tier 0-2 with single icon)
  }
  const svgWidth = getSvgWidth()

  // Bug 479023: Adjust X positions when Design button is hidden (Active status)
  // Bug 479123 Issue 18: Obsolete button at first position when it's the only icon
  const activeButtonX = showDesignButton ? ACTIVE_X : DESIGN_X
  const obsoleteButtonX = showActiveButton
    ? showDesignButton
      ? OBSOLETE_X
      : ACTIVE_X
    : DESIGN_X

  // ActiveButton component expects a curried onClick: onClick(actionType) => eventHandler
  const handleActivateClick = () => () => {
    if (canClickActive && onActivateClick) {
      onActivateClick()
    }
  }

  // ObsoleteButton expects a direct onClick handler (not curried)
  const handleObsoleteClick = () => {
    if (canClickObsolete && onObsoleteClick) {
      onObsoleteClick()
    }
  }

  // No-op functions matching expected patterns
  const noopCurried = () => () => {}
  const noop = () => {}

  return (
    <Container>
      <StatusTitle>
        {strings.feature_collection_status}:{' '}
        <StatusText>{getStatusLabel(status)}</StatusText>
      </StatusTitle>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={svgWidth}
        height={showLabels ? 52 : 34}
        viewBox={`0 0 ${svgWidth} ${showLabels ? 52 : 34}`}
      >
        <g fill="none" fillRule="evenodd">
          {/* Connector line: Design -> Active (only if both buttons are shown) */}
          {showDesignToActiveLine && (
            <PhaseConnector
              isDisabled={statusIndex < 1}
              strokeLinecap="square"
              d={`M${DESIGN_X + CIRCLE_RADIUS + 2} 17 H${
                activeButtonX - CIRCLE_RADIUS - 2
              }`}
            />
          )}
          {/* Connector line: Active -> Obsolete (only if Obsolete button is shown) */}
          {showActiveToObsoleteLine && (
            <PhaseConnector
              isDisabled={statusIndex < 2}
              strokeLinecap="square"
              d={`M${activeButtonX + CIRCLE_RADIUS + 2} 17 H${
                obsoleteButtonX - CIRCLE_RADIUS - 2
              }`}
            />
          )}

          {/* Design Button - Bug 479023: Only shown in Design or Obsolete status, not Active */}
          {showDesignButton && (
            <DesignButton
              isSelected={true}
              isSelectable={false}
              onClick={noopCurried}
              transformX={DESIGN_X - CIRCLE_RADIUS}
              transformY={4}
              withLabel={false}
            />
          )}

          {/* Active Button - shown if user has activate permission OR already in Active/Obsolete state */}
          {/* When already selected (isActive), disable hover by setting isSelectable=false */}
          {showActiveButton && (
            <ActiveButton
              isSelected={isActive}
              isSelectable={canClickActive && !isActive}
              onClick={canClickActive ? handleActivateClick : noopCurried}
              transformX={activeButtonX - CIRCLE_RADIUS}
              transformY={4}
              withLabel={false}
            />
          )}

          {/* Obsolete Button - shown if user has obsolete permission OR already in Obsolete state */}
          {/* When already selected (isObsolete), disable hover by setting isSelectable=false */}
          {showObsoleteButton && (
            <ObsoleteButton
              isSelected={isObsolete}
              isSelectable={canClickObsolete && !isObsolete}
              onClick={canClickObsolete ? handleObsoleteClick : noop}
              transformX={obsoleteButtonX - CIRCLE_RADIUS}
              transformY={4}
              withLabel={false}
            />
          )}

          {/* Labels - only shown when multiple icons are visible (Tier 2+) */}
          {showLabels && (
            <>
              {showDesignButton && (
                <StatusLabel x={DESIGN_X} y="47" isActive={statusIndex >= 0}>
                  {strings.design || 'Design'}
                </StatusLabel>
              )}
              {showActiveButton && (
                <StatusLabel
                  x={activeButtonX}
                  y="47"
                  isActive={isActive || canClickActive}
                >
                  {/* Bug 479697: Per Figma, show "Activate" when it's an action (Design mode),
                      "Active" when it's the current status */}
                  {canClickActive
                    ? strings.activate || 'Activate'
                    : strings.active || 'Active'}
                </StatusLabel>
              )}
              {showObsoleteButton && (
                <StatusLabel
                  x={obsoleteButtonX}
                  y="47"
                  isActive={statusIndex >= 2 || canClickObsolete}
                >
                  {strings.obsolete || 'Obsolete'}
                </StatusLabel>
              )}
            </>
          )}
        </g>
      </svg>
    </Container>
  )
}

LifecycleStatusVisual.propTypes = {
  status: PropTypes.oneOf([DESIGN, ACTIVE, OBSOLETE]),
  onActivateClick: PropTypes.func,
  onObsoleteClick: PropTypes.func,
}

LifecycleStatusVisual.defaultProps = {
  status: DESIGN,
  onActivateClick: null,
  onObsoleteClick: null,
}

export default LifecycleStatusVisual
